import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./payment-method-modal.module.scss";
import CommonDialog from "../../../../common/components/dialog/CommonDialog";
import Button from "../../../../common/ekedc-components/button/Button";
import { FriendRequestIcon } from "../../../../assets/icons";

export type PaymentMethodKey = "card" | "wallet" | "transfer" | "borrow" | "askAFriend";

export interface PaymentMethodRow {
	key: PaymentMethodKey;
	/** Right-side pill, e.g. wallet balance or borrow max limit. */
	pill?: { label: string; amount: string };
	disabled?: boolean;
}

export interface RecapRow {
	label: string;
	value: string;
}

interface PaymentMethodModalProps {
	title?: string;
	/** Big formatted amount, e.g. "₦50,000". */
	amountText: string;
	/** Formatted outstanding-debt amount; when set, the debt pill is shown. */
	debtText?: string;
	/** Optional transaction recap rows (feature-specific summary). */
	recap?: RecapRow[];
	methods: PaymentMethodRow[];
	payLabel: string;
	onClose: () => void;
	onPay: (method: PaymentMethodKey) => void;
	/** Called when a disabled method is tapped (e.g. to show a toast). */
	onDisabledSelect?: (method: PaymentMethodKey) => void;
}

const CardIcon: React.FC = () => (
	<svg width="22" height="22" viewBox="0 0 22 22" fill="none">
		<rect x="1.83" y="3.67" width="18.33" height="14.67" rx="2" stroke="var(--app-primary-text-08)" strokeWidth="1.5" fill="none" />
		<path d="M1.83 8.25h18.33" stroke="var(--app-primary-text-08)" strokeWidth="1.5" />
	</svg>
);

const WalletIcon: React.FC = () => (
	<svg width="22" height="22" viewBox="0 0 22 22" fill="none">
		<path d="M3 7.5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-8Z" stroke="var(--app-primary-text-08)" strokeWidth="1.5" fill="none" />
		<circle cx="16" cy="11.5" r="1.25" fill="var(--app-primary-text-08)" />
	</svg>
);

const BorrowIcon: React.FC = () => (
	<svg width="22" height="22" viewBox="0 0 22 22" fill="none">
		<path d="M5.5 8.5C5.5 7.4 6.4 6.5 7.5 6.5h7c1.1 0 2 .9 2 2v6c0 1.1-.9 2-2 2h-7c-1.1 0-2-.9-2-2v-6Z" stroke="var(--app-primary-text-08)" strokeWidth="1.5" fill="none" />
		<path d="M8.5 11h5" stroke="var(--app-primary-text-08)" strokeWidth="1.5" strokeLinecap="round" />
		<path d="M8.5 13.5h3" stroke="var(--app-primary-text-08)" strokeWidth="1.5" strokeLinecap="round" />
	</svg>
);

const TransferIcon: React.FC = () => (
	<svg width="22" height="22" viewBox="0 0 22 22" fill="none">
		<path d="M5.5 8.25h11M5.5 8.25l3-3M16.5 13.75h-11M16.5 13.75l-3 3" stroke="var(--app-primary-text-08)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
	</svg>
);

const CrossIcon: React.FC = () => (
	<svg width="22" height="22" viewBox="0 0 22 22" fill="none">
		<path d="M5.5 5.5L16.5 16.5M16.5 5.5L5.5 16.5" stroke="var(--app-primary-text)" strokeWidth="1.5" strokeLinecap="round" />
	</svg>
);

const PaymentMethodModal: React.FC<PaymentMethodModalProps> = ({
	title = "Payment",
	amountText,
	debtText,
	recap,
	methods,
	payLabel,
	onClose,
	onPay,
	onDisabledSelect,
}) => {
	const { t } = useTranslation();
	const [selected, setSelected] = useState<PaymentMethodKey | null>(null);

	const METHOD_META: Record<PaymentMethodKey, { icon: React.FC; label: string }> = {
		card: { icon: CardIcon, label: "Pay with Card" },
		wallet: { icon: WalletIcon, label: "Pay with Wallet" },
		transfer: { icon: TransferIcon, label: "Pay with Transfer" },
		borrow: { icon: BorrowIcon, label: "Borrow Units" },
		askAFriend: { icon: FriendRequestIcon, label: t("payment.askAFriendToPay") },
	};

	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [onClose]);

	const handleSelect = (row: PaymentMethodRow) => {
		if (row.disabled) {
			onDisabledSelect?.(row.key);
			return;
		}
		setSelected(row.key);
	};

	const handlePay = () => {
		if (selected) onPay(selected);
	};

	return (
		<CommonDialog onCloseDialog={onClose} noPadding borderRadius="21.67px" bottomSheetOnMobile>
			<div className={styles.modal}>
				<header className={styles.toolbar}>
					<button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
						<CrossIcon />
					</button>
					<h2 className={styles.title}>{title}</h2>
					<div className={styles.toolbarSpacer} />
				</header>

				<div className={styles.body}>
					<div className={styles.amountBlock}>
						<span className={styles.amountLabel}>Amount</span>
						<span className={styles.amountValue}>{amountText}</span>
						{debtText && (
							<div className={styles.debtPill}>
								<span className={styles.debtPillLabel}>Outstanding Debt</span>
								<span className={styles.debtPillDivider} />
								<span className={styles.debtPillAmount}>{debtText}</span>
							</div>
						)}
					</div>

					{recap && recap.length > 0 && (
						<div className={styles.recapCard}>
							{recap.map((row, i) => (
								<div className={styles.recapRow} key={i}>
									<span className={styles.recapLabel}>{row.label}</span>
									<span className={styles.recapValue}>{row.value}</span>
								</div>
							))}
						</div>
					)}

					<div className={styles.methodsBlock}>
						<h3 className={styles.methodsHeader}>Choose Payment Method</h3>
						<div className={styles.methodsList}>
							{methods.map(row => {
								const Meta = METHOD_META[row.key];
								const Icon = Meta.icon;
								const isActive = selected === row.key;
								return (
									<button
										type="button"
										key={row.key}
										className={`${styles.methodRow} ${isActive ? styles.methodRowActive : ""} ${row.disabled ? styles.methodRowDisabled : ""}`}
										onClick={() => handleSelect(row)}
									>
										<span className={styles.methodLeft}>
											<Icon />
											<span className={styles.methodLabel}>{Meta.label}</span>
										</span>
										<span className={styles.methodRight}>
											{row.pill && (
												<span className={styles.methodPill}>
													<span className={styles.methodPillLabel}>{row.pill.label}</span>
													<span className={styles.methodPillAmount}>{row.pill.amount}</span>
												</span>
											)}
											<span className={`${styles.radio} ${isActive ? styles.radioActive : ""}`} />
										</span>
									</button>
								);
							})}
						</div>
					</div>

					<Button text={payLabel} onClick={handlePay} disabled={selected === null} />
				</div>
			</div>
		</CommonDialog>
	);
};

export default PaymentMethodModal;
